import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, Banknote, Download } from "lucide-react";
import { useHubNIHGrants } from "@/hooks/useHubResearch";

interface NIHGrantsTabProps {
  accountId: string;
  companyName: string;
  hubAccountName: string | null;
}

export function NIHGrantsTab({ accountId, companyName, hubAccountName }: NIHGrantsTabProps) {
  const { data: grants, isLoading } = useHubNIHGrants(companyName, hubAccountName);

  const stats = useMemo(() => {
    if (!grants) return { total: 0, totalFunding: 0, active: 0 };
    const totalFunding = grants.reduce((sum, g) => sum + (g.award_amount || 0), 0);
    const active = grants.filter(g => g.is_active).length;
    return { total: grants.length, totalFunding, active };
  }, [grants]);

  if (isLoading) return <div className="flex items-center justify-center h-[300px]"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  if (!grants?.length) {
    return (
      <div className="p-8 text-center">
        <Banknote className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">No NIH Grants Found</h3>
        <p className="text-muted-foreground text-sm">No NIH grants found for {companyName} in the research database</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Overview Stats */}
      <div>
        <h3 className="text-lg font-semibold mb-4">NIH Grants Overview</h3>
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-blue-50/50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-700">{stats.total}</p>
              <p className="text-sm text-muted-foreground">Total Grants</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50/50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-700">${(stats.totalFunding / 1_000_000).toFixed(1)}M</p>
              <p className="text-sm text-muted-foreground">Total Funding</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50/50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-700">{stats.active}</p>
              <p className="text-sm text-muted-foreground">Active Grants</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Grants Table */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-lg font-semibold">Grants ({grants.length})</h4>
          <Button variant="outline" size="sm" className="gap-2">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Project Number</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>PI Names</TableHead>
              <TableHead>Award Amount</TableHead>
              <TableHead>Fiscal Year</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {grants.map((grant) => (
              <TableRow key={grant.id}>
                <TableCell className="font-mono text-xs">{grant.project_number || "—"}</TableCell>
                <TableCell><span className="text-sm line-clamp-2">{grant.title || "—"}</span></TableCell>
                <TableCell className="text-sm text-muted-foreground">{grant.pi_names?.join(", ") || "—"}</TableCell>
                <TableCell className="text-sm font-medium text-emerald-600">
                  {grant.award_amount ? `$${Number(grant.award_amount).toLocaleString()}` : "—"}
                </TableCell>
                <TableCell className="text-sm">{grant.fiscal_year || "—"}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-xs ${grant.is_active ? "bg-green-100 text-green-800 border-green-200" : "bg-gray-100 text-gray-600 border-gray-200"}`}>
                    {grant.is_active ? "Active" : "Completed"}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
